import { useState } from "react";
import { Linkedin, Twitter, Facebook, Mail, MessageCircle, Copy, Check, Share2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Social share buttons — single row of platform-specific deep-links.
 * Each platform opens its native share dialog with prefilled title + URL.
 * Falls back to clipboard copy when no platform handler is registered.
 */
export const SocialShareButtons = ({ url, title, compact = false }) => {
  const [copied, setCopied] = useState(false);

  const encoded = {
    url: encodeURIComponent(url),
    title: encodeURIComponent(title),
    body: encodeURIComponent(`${title}\n\n${url}`),
  };

  const channels = [
    {
      id: "linkedin",
      label: "LinkedIn",
      Icon: Linkedin,
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encoded.url}`,
      color: "hover:text-[#0A66C2] hover:border-[#0A66C2]",
    },
    {
      id: "x",
      label: "X",
      Icon: Twitter,
      href: `https://twitter.com/intent/tweet?text=${encoded.title}&url=${encoded.url}`,
      color: "hover:text-black hover:border-black",
    },
    {
      id: "facebook",
      label: "Facebook",
      Icon: Facebook,
      href: `https://www.facebook.com/sharer/sharer.php?u=${encoded.url}`,
      color: "hover:text-[#1877F2] hover:border-[#1877F2]",
    },
    {
      id: "whatsapp",
      label: "WhatsApp",
      Icon: MessageCircle,
      href: `https://wa.me/?text=${encoded.body}`,
      color: "hover:text-[#25D366] hover:border-[#25D366]",
    },
    {
      id: "mail",
      label: "E-mail",
      Icon: Mail,
      href: `mailto:?subject=${encoded.title}&body=${encoded.body}`,
      color: "hover:text-[#2d8cf0] hover:border-[#2d8cf0]",
    },
  ];

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link gekopieerd");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Kopiëren mislukt");
    }
  };

  const trackClick = (id) => {
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.debug("[share]", id);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2 no-print" data-testid="social-share-buttons">
      {!compact && (
        <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase text-gray-500 mr-1">
          <Share2 className="w-3 h-3" /> Deel:
        </span>
      )}
      {channels.map((c) => (
        <a
          key={c.id}
          href={c.href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackClick(c.id)}
          title={`Deel via ${c.label}`}
          className={`inline-flex items-center justify-center w-8 h-8 border border-gray-300 text-gray-600 transition-colors ${c.color}`}
          data-testid={`share-${c.id}`}
        >
          <c.Icon className="w-4 h-4" />
        </a>
      ))}
      <button
        onClick={copyLink}
        title="Kopieer link"
        className="inline-flex items-center justify-center w-8 h-8 border border-gray-300 text-gray-600 hover:border-gray-700 hover:text-gray-900 transition-colors"
        data-testid="share-copy"
      >
        {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
      </button>
    </div>
  );
};
